import {
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from "react";
import "./style.css";
import send from "../../Assets/icon/Send.svg";
import TooltipContent from "../Tooltip/ToolTipContent";
import { Tooltip } from "react-tooltip";
import ReactDOMServer from 'react-dom/server';
import { ReactComponent as Mic } from "../../Assets/icon/MicrophoneOutline24.svg";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";

const ChatInput = forwardRef((props, ref) => {
  const {
    onChange,
    changeData,
    value,
    isSurvey,
    isChatProgress,
    isChatLoading,
    chatPlaceHolder = "Write here...",
    // microphone controls
    isRecording,
    startRecording,
    // privacy mode
    isPrivacyActivated,
  } = props;

  const [chatInput, setchatInput] = useState(value);
  const inputRef = useRef(null); // textarea ref for auto-resize
  const maxHeight = 131; // max height for textarea
  const initialHeight = 57; // minimum height as required

  // Auto-focus only on Desktop/Tablet (width > 478px)
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth > 478 && !isChatLoading) {
      inputRef.current?.focus({ preventScroll: true });
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If loading state changes to not loading, try focusing (desktop/tablet only)
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth > 478 && !isChatLoading) {
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [isChatLoading]);

  useEffect(() => {
    setchatInput(value);
    // Force initial height when value changes
    setTimeout(() => {
      if (inputRef.current && !value) {
        inputRef.current.style.height = `${initialHeight}px`;
      }
    }, 0);
  }, [value]);

  // Auto-resize function
  const adjustHeight = () => {
    const textarea = inputRef.current;
    if (textarea) {
      // Reset height to get accurate scrollHeight
      textarea.style.height = `${initialHeight}px`;

      const scrollHeight = textarea.scrollHeight;

      // Only expand if scrollHeight is significantly larger than initial height
      // This accounts for the padding and ensures we only expand when text actually wraps
      if (scrollHeight > initialHeight + 2) {
        const newHeight = Math.min(scrollHeight, maxHeight);
        textarea.style.height = `${newHeight}px`;
        textarea.style.overflowY = newHeight >= maxHeight ? "auto" : "hidden";
      } else {
        // Keep at initial height
        textarea.style.height = `${initialHeight}px`;
        textarea.style.overflowY = "hidden";
      }
    }
  };

  // Adjust height when content changes
  useEffect(() => {
    adjustHeight();
  }, [chatInput]); // Only depend on chatInput, not value

  const handleOnChange = (e) => {
    setchatInput(e.target.value);
    changeData && changeData(e.target.value);
  };

  useImperativeHandle(ref, () => ({
    clearInput() {
      setchatInput("");
      // Reset height after clearing with a slight delay
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.style.height = `${initialHeight}px`;
          inputRef.current.style.overflowY = "hidden";
        }
      }, 0);
    },
  }));

  const handleSubmit = async () => {
    if (chatInput && !isChatLoading) {
      await onChange(chatInput);
    }
  };

  const handleEnterPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey && !isChatLoading) {
      event.preventDefault(); // Prevent new line
      handleSubmit();
    }
    // Allow Shift+Enter for new line
  };

  const renderMicIcon = () =>
    !!startRecording &&
    !isSurvey && (
      <div
        className="inline-mic-icon tooltip-container"
        onClick={startRecording}
        data-tooltip-id="relationship-tooltip-anchor-lock-microphone"
        data-tooltip-html={ReactDOMServer.renderToStaticMarkup(<TooltipContent type="microphone-icon" />)}
      >
        {isRecording ? (
          <StopCircleOutlinedIcon className="inline-stop-icon" />
        ) : (
          <Mic className="inline-mic-svg" />
        )}
        <Tooltip
          id="relationship-tooltip-anchor-lock-microphone"
          place="bottom"
          className="aris-tooltip"
        />
      </div>
    );

  return (
    <div className={`chat-input ${isPrivacyActivated ? "privacy-active" : ""} ${isChatLoading ? "chat-input-loading" : ""}`}>
      <textarea
        ref={inputRef} // Add the ref back for auto-resize
        value={value || chatInput}
        onChange={handleOnChange}
        onKeyDown={handleEnterPress}
        placeholder={
          isChatLoading
            ? "Loading..."
            : chatPlaceHolder
        }
        disabled={isChatLoading}
        rows={1}
        className={`textarea-input ${isPrivacyActivated ? "privacy-active" : ""}`}
        style={{
          minHeight: `${initialHeight}px`,
          maxHeight: `${maxHeight}px`,
          height: `${initialHeight}px`,
          resize: "none",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      />

      {isChatLoading ? (
        <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "flex-end", width: "100%", gap: 1 }}>
          {renderMicIcon()}

          {/* Loading spinner at the far right (absolute, no layout space) */}
          <div style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
            <div className="loading-spinner">...</div>
          </div>
        </div>
      ) : isChatProgress && !chatInput ? (
        <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
          {renderMicIcon()}
          {/* Hide send arrow entirely in this state to avoid taking space */}
        </div>
      ) : chatInput ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {renderMicIcon()}

          <div
            className={`top-arrow-container ${Math.min(inputRef?.current?.scrollHeight, maxHeight) > 52 &&
              `align-bot`
              }`}
            onClick={(e) => {
              e.stopPropagation();
              handleSubmit();
            }}
          >
            <img src={send} alt="send" />
          </div>
        </div>
      ) : (
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 1 }}>
          {renderMicIcon()}
        </div>
      )}
    </div>
  );
});

export default ChatInput;
